import { waitForElementPresence, scrollToElement } from "@al/vr-test-helper";
import { ElementFinder, browser, by, element } from 'protractor';

export class ALNotificationHelper {
    container: ElementFinder = element(by.css('al-cardstack div.al-card-stack div.al-head-list'));
    loading: ElementFinder = this.container.element(by.css('div.al-loading'));
    // Filters
    filters: ElementFinder = element(by.css('div.cardstack-sidenav'));
    stateFilters: ElementFinder = this.filters.element(by.css('al-state-filter ul.ui-listbox-list'));
    // Content menu
    tabMenu: ElementFinder = element(by.css('al-archipeligo19-content-menu'));
    // Header
    header: ElementFinder = this.container.element(by.css('al-content-header'));
    // Toolbar
    toolbar: ElementFinder = this.container.element(by.css('al-content-toolbar'));
    checkAll: ElementFinder = this.container.element(by.css('p-checkbox.content-toolbar-checkall'));
    sortSelector: ElementFinder = this.toolbar.element(by.css('p-dropdown.content-toolbar-sort'));
    sortDirection: ElementFinder = this.toolbar.element(by.css('i.content-toolbar-sort-direction'));
    searchInput: ElementFinder = this.toolbar.element(by.css('div.al-search input#al-search-input'));
    // list
    list: ElementFinder = this.container.element(by.css('div.al-list'));
    // BottomSheet
    bottomSheet: ElementFinder = element(by.css('div.ui-shadow.ui-sidebar-bottom.ui-sidebar-full'));
    notificationForm: ElementFinder = this.bottomSheet.element(by.css('div.container'));
    // Snackbar Actions
    snackbar: ElementFinder = element(by.css('al-action-snackbar'));

    getCardByIndex(index: number): ElementFinder {
        waitForElementPresence(this.list, 'The notification list is not present');
        const notification = this.list.all(by.css('al-base-card')).get(index);
        waitForElementPresence(notification, 'The notification list is not present');
        return notification;
    }

    toggleCardByIndex(index: number): void {
        const notification = this.getCardByIndex(index);
        const button = notification.element(by.css('p-header div.e2e-al-base-card-toggle'));
        waitForElementPresence(button, "The expand/collapse button is not present");
        button.click();
    }

    async executeFooterActionByIndexAndEvent(index: number, event: string): Promise<void> {
        const notification = this.getCardByIndex(index);
        const footerCard = notification.element(by.css('p-footer'));
        if (await footerCard.isDisplayed()) {
            scrollToElement(footerCard);
            const button = footerCard.element(by.css(`.e2e-base-card-footer-action-${event}`));
            button.click().then(() => browser.sleep(1000));
            waitForElementPresence(button, `The action ${event} is not present`);
        } else {
            this.toggleCardByIndex(index);
            this.executeFooterActionByIndexAndEvent(index, event);
        }
    }

    async isbottomSheetShown(): Promise<boolean> {
        return await this.bottomSheet.isDisplayed();
    }

    async getBottomSheetTitle(): Promise<string> {
        return await this.bottomSheet.element(by.css('div.header p')).getText();
    }

    getNotificationFormSection(section: string): ElementFinder {
        const sectionElm = this.notificationForm.element(by.cssContainingText('h1.columnHeader', section));
        waitForElementPresence(sectionElm, `The notification form ${section} is not present`);
        return sectionElm;
    }

    getNotificationFormInputByName(name: string): ElementFinder {
        return this.notificationForm.element(by.css(`*[name="${name}"]`));
    }

    changeRecipientTypeByType(type: string) {
        const option = this.notificationForm.element(by.cssContainingText('al-menu-list ul li', type));
        option.click().then(() => browser.sleep(300));
    }

    async isPrimaryActionEnabled(): Promise<boolean> {
        return await this.bottomSheet.element(by.css('div.header button.primaryAction')).isEnabled();
    }

    async isUpdateActionEnabled(): Promise<boolean> {
        return await this.bottomSheet.element(by.cssContainingText('div.header button', 'Update')).isEnabled();
    }

    async closeNotificationForm(): Promise<void> {
        await this.bottomSheet.element(by.cssContainingText('div.header button span', 'Cancel')).click();
        await browser.sleep(300);
    }

    async changeTabByName(tab: 'Alert Notifications' | 'Schedules'): Promise<void> {
        await waitForElementPresence(this.tabMenu, "The tabs are not present");
        const tabElem = this.tabMenu.element(by.cssContainingText('span a', tab));
        await tabElem.click();
        await browser.sleep(1700);
    }

    async openSort(){
        waitForElementPresence(this.sortSelector, 'Sort selector is not present');
        await this.sortSelector.click();
    }

    async selectSortByName(name: string): Promise<void>{
        const panel = this.sortSelector.element(by.css('div.ui-dropdown-panel'));
        if(await panel.isPresent()){
            const item = panel.element(by.cssContainingText('div.ui-dropdown-items-wrapper ul p-dropdownitem', name));
            item.click();
        }else{
            this.openSort();
            this.selectSortByName(name);
        }
    }

    async toggleSortDirection(): Promise<void> {
        await this.sortDirection.click();
        await browser.sleep(300);
    }

    async setSearch(query: string): Promise<void> {
        await this.searchInput.sendKeys(query);
        await browser.sleep(300);
    }

    async clearSearch() {
        await this.searchInput.clear();
    }

    async getActiveTabMenu(): Promise<string> {
        return await this.tabMenu.element(by.css('span.activated')).getText();
    }

    async isActionSnackbarVisible(): Promise<boolean> {
        return await this.snackbar.element(by.css('div.al-action-snackbar')).isPresent();
    }

    async toggleCheckAll(): Promise<void> {
        await this.checkAll.click();
        await browser.sleep(500);
    }

    async changeStateFilter(state: 'Active' | 'Inactive'): Promise<void> {
        if(!(await this.getStateFilterSelected()).includes(state)){
            const stateElem = this.stateFilters.element(by.css('li:not(.ui-state-highlight)'));
            stateElem.click().then(() => browser.sleep(100));
        }
        return;
    }

    async getStateFilterSelected(): Promise<string> {
        return await this.stateFilters.element(by.css('li.ui-state-highlight > div > span')).getText();
    }

}
